import { subject } from "@casl/ability";
import { useQueryMember } from "@gymflow/api";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Route, useHistory, useParams } from "react-router-dom";

import usersIcon from "../../assets/img/users-icon.svg";
import { Button } from "../components/atoms";
import PillTabsLayout from "../components/molecules/PillTabsLayout";
import { ActionMenuButtons } from "../components/SearchGlobal/ActionMenuButtons";
import { ShopSidebarContext } from "../components/Shop/ShopSidebar/ShopSidebarContext";
import { UserMemberNotes } from "../components/UserMember/Notes/UserMemberNotes";
import TimelineWrapper from "../components/UserMember/Timeline/Timeline";
import { UserBookings } from "../components/UserMember/UserBookings";
import { UserMemberMembership } from "../components/UserMember/UserMemberMembership/UserMemberMembership";
import { UserMemberPayments } from "../components/UserMember/UserMemberPayments/UserMemberPayments";
import { UserMemberProfile } from "../components/UserMember/UserMemberProfile";
import { UserMemberVisits } from "../components/UserMember/UserMemberVisits/UserMemberVisits";
import { UserSessions } from "../components/UserMember/UserSessions/UserSessions";
import { usePortalRoutes } from "../hooks/usePortalRoutes";
import { Can, Subject, Verb } from "../permissions";
import { PageTitleProviderContext } from "../providers";
import useGymflowModels from "../store";

export const UserMemberPage = {
  Profile: "/profile",
  Account: "/account",
  Sessions: "/sessions",
  Bookings: "/bookings",
  Notes: "/notes",
  Payments: "/payments",
  Timeline: "/timeline",
  Visits: "/visits",
};

export function UserMember() {
  const history = useHistory();

  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { createMemberLink } = usePortalRoutes();
  const { id } = useParams<{ id: string }>();

  const { data: member } = useQueryMember({
    api,
    memberId: id,
  });
  const { setPageTitle } = useContext(PageTitleProviderContext);
  useEffect(() => {
    if (member?.firstName && member?.lastName) {
      setPageTitle({
        title: `${member.firstName} ${member.lastName}`,
        iconUrl: usersIcon,
      });
    }
  }, [setPageTitle, member?.firstName, member?.lastName]);

  const { showShop } = useContext(ShopSidebarContext);
  return (
    <>
      <Can not I={Verb.View} a={subject(Subject.MemberProfile, { ...member })}>
        <div className="content flex size-full max-h-full flex-col p-8">
          You are not authorized to view this member&apos;s profile.
          <Button
            onClick={(e) => {
              e.preventDefault();
              history.goBack();
            }}
          >
            Go back
          </Button>
        </div>
      </Can>
      <Can I={Verb.View} a={subject(Subject.MemberProfile, { ...member })}>
        <Route path={createMemberLink(id)}>
          <div className="relative flex size-full">
            <PillTabsLayout
              enableAnimations
              className="absolute inset-0 p-4 lg:p-8"
              tabs={[
                {
                  text: t("page.userMember.tabTitle.profile"),
                  path: createMemberLink(id, UserMemberPage.Profile),
                  component: <UserMemberProfile memberId={id} />,
                },
                {
                  text: t("page.userMember.tabTitle.membership"),
                  path: createMemberLink(id, UserMemberPage.Account),
                  component: <UserMemberMembership memberId={id} />,
                },
                {
                  text: t("page.userMember.tabTitle.sessions"),
                  path: createMemberLink(id, UserMemberPage.Sessions),
                  component: <UserSessions memberId={id} />,
                },
                {
                  text: t("page.userMember.tabTitle.bookings"),
                  path: createMemberLink(id, UserMemberPage.Bookings),
                  component: <UserBookings memberId={id} />,
                },
                {
                  text: t("page.userMember.tabTitle.notes"),
                  path: createMemberLink(id, UserMemberPage.Notes),
                  component: <UserMemberNotes memberId={id} />,
                },
                {
                  text: t("page.userMember.tabTitle.payments"),
                  path: createMemberLink(id, UserMemberPage.Payments),
                  component: <UserMemberPayments memberId={id} />,
                },
                {
                  text: t("page.userMember.tabTitle.timeline"),
                  path: createMemberLink(id, UserMemberPage.Timeline),
                  component: <TimelineWrapper memberId={id} />,
                },
                {
                  text: t("page.userMember.tabTitle.visits"),
                  path: createMemberLink(id, UserMemberPage.Visits),
                  component: <UserMemberVisits memberId={id} />,
                },
              ]}
              moreActions={
                <ActionMenuButtons userMemberId={id} showShop={showShop} />
              }
            />
          </div>
        </Route>
      </Can>
    </>
  );
}
